import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./HomePage.css";

import cdacLogo from "../images/cdac-csmart.png";
import csmartLogo from "../images/csmartdark.png";

import imgSocial from "./HomePageImg/SocialMediaIntelligence.png";
import imgNews from "./HomePageImg/NewsIntelligence.png";
import imgMisinfo from "./HomePageImg/MisinformationDetection.png";
import imgSentiment from "./HomePageImg/SentimentAnalysis.png";
import imgImage from "./HomePageImg/ImageAnalysis.png";
import imgCrawling from "./HomePageImg/AutomatedCrawling.png";

import CslGlobe from "./CslGlobe";
import HomePageFooter from "./HomePageFooter";

/* ═══════════════════════════════════════════════════════
   FEATURE SLIDES
   ═══════════════════════════════════════════════════════ */
const FEATURES = [
  {
    color: "#3572ef",
    title: "Social Media Intelligence",
    desc: "Monitor conversations, hashtags, and narratives across Facebook, Instagram, X, Telegram, YouTube, and more — with second-by-second precision.",
    icon: <IconChat />,
    img: imgSocial,
  },
  {
    color: "#7c3aed",
    title: "News Intelligence",
    desc: "Automated crawling across 50+ national and international publications. Never miss a story that shapes public discourse.",
    icon: <IconNews />,
    img: imgNews,
  },
  {
    color: "#dc2626",
    title: "Misinformation Detection",
    desc: "AI-powered identification of false narratives, deepfakes, and coordinated inauthentic behaviour before they go viral.",
    icon: <IconShield />,
    img: imgMisinfo,
  },
  {
    color: "#0891b2",
    title: "Sentiment Analysis",
    desc: "Understand how public opinion shifts in real time. Track positive, negative, and neutral sentiment across any topic or event.",
    icon: <IconPulse />,
    img: imgSentiment,
  },
  {
    color: "#059669",
    title: "Image Analysis",
    desc: "Detect visually manipulated media, propaganda imagery, and out-of-context photographs with multi-layered AI inspection.",
    icon: <IconImage />,
    img: imgImage,
  },
  {
    color: "#d97706",
    title: "Automated Crawling",
    desc: "Schedule intelligent crawlers across platforms and keywords. Collect structured data at scale without manual intervention.",
    icon: <IconBot />,
    img: imgCrawling,
  },
];

const SLIDE_MS = 5000;

/* ═══════════════════════════════════════════════════════
   STAT STRIP — fills the empty space under the CTA.
   Static figures, no live data. Keep to 4; a 5th wraps
   awkwardly on narrow laptops.
   ═══════════════════════════════════════════════════════ */
const STATS = [
  { num: "30+", label: "Platforms" },
  { num: "50+", label: "News Sources" },
  { num: "Real-time", label: "Processing" },
  { num: "AI-Powered", label: "Analysis Engine" },
];

/* ─────────────────────── page ─────────────────────── */

export default function HomePage() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  /* lock document scroll for this page only */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = { htmlOY: html.style.overflowY, bodyOY: body.style.overflowY };
    html.style.overflowY = "hidden";
    body.style.overflowY = "hidden";
    return () => {
      html.style.overflowY = prev.htmlOY;
      body.style.overflowY = prev.bodyOY;
    };
  }, []);

  /* auto-advance */
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(
      () => setActive((a) => (a + 1) % FEATURES.length),
      SLIDE_MS,
    );
    return () => clearInterval(t);
  }, [paused, active]);

  const goTo = useCallback((i) => {
    setActive(((i % FEATURES.length) + FEATURES.length) % FEATURES.length);
  }, []);

  const feature = FEATURES[active];

  return (
    <div className="csl">
      {/* ── ambient background ── */}
      <div className="csl-bg" aria-hidden="true">
        <div className="csl-bg__orb csl-bg__orb--a" />
        <div
          className="csl-bg__orb csl-bg__orb--b"
          style={{
            background: `radial-gradient(circle, ${feature.color}1f 0%, transparent 66%)`,
          }}
        />
        <div className="csl-bg__orb csl-bg__orb--c" />

        {/* live spinning Earth */}
        <CslGlobe opacity={0.42} speed={1} />

        <div className="csl-bg__grid" />
      </div>

      {/* ── brand mark, top-left — mirrors Sign In on the right ── */}
      <div className="csl-lockup">
        <img src={cdacLogo} alt="CDAC" className="csl-lockup__cdac" />
        {/* <span className="csl-lockup__sep" aria-hidden="true" />
        <img src={csmartLogo} alt="C-SMART" className="csl-lockup__csmart" /> */}
      </div>

      {/* ── floating Sign In (the navbar is gone) ── */}
      <button
        type="button"
        className="csl-signin"
        onClick={() => navigate("/login")}
      >
        Sign&nbsp;In
        <ArrowRight />
      </button>

      {/* ── Main ── */}
      <main className="csl-main">
        {/* left: pitch */}
        <div className="csl-left">
          <h1 className="csl-h1">
            The Intelligence Behind
            <br />
            <span className="csl-h1__grad">India&rsquo;s Digital Pulse</span>
          </h1>

          <p className="csl-lead">
            CSmart transforms social media data into AI-powered intelligence
            &mdash; detecting sentiment, misinformation, fake news, visual
            narratives, and emerging trends at scale. Stay ahead of narratives,
            identify threats, and uncover opportunities before they become
            obvious. Monitor. Analyze. Predict. Act.
          </p>

          <div className="csl-actions">
            <button
              type="button"
              className="csl-btn csl-btn--primary"
              onClick={() => navigate("/login")}
            >
              Access Platform
              <ArrowRight />
            </button>
          </div>

          {/* stat strip — anchors the bottom of the column */}
          <dl className="csl-stats">
            {STATS.map((s, i) => (
              <div className="csl-stat" key={i}>
                <dt className="csl-stat__num">{s.num}</dt>
                <dd className="csl-stat__lbl">{s.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* right: feature carousel — reverted to the original
            media + caption card */}
        <div
          className="csl-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          aria-roledescription="carousel"
          aria-label="Platform capabilities"
        >
          <div
            className="csl-track"
            style={{ transform: `translateX(-${active * 100}%)` }}
          >
            {FEATURES.map((f, i) => (
              <section
                className={`csl-slide${i === active ? " is-active" : ""}`}
                key={i}
                style={{ "--sl-color": f.color }}
                aria-hidden={i !== active}
              >
                <div className="csl-slide__media">
                  {f.img ? (
                    <img
                      src={f.img}
                      alt={f.title}
                      className="csl-slide__img"
                      loading={i === 0 ? "eager" : "lazy"}
                      decoding="async"
                      draggable="false"
                    />
                  ) : (
                    <div className="csl-slide__ph">
                      <div className="csl-slide__ph-icon">{f.icon}</div>
                      <span className="csl-slide__ph-label">
                        Feature preview
                      </span>
                    </div>
                  )}
                </div>

                <div className="csl-slide__caption">
                  <div className="csl-slide__chip">
                    <span className="csl-slide__chip-icon">{f.icon}</span>
                    {String(i + 1).padStart(2, "0")} /{" "}
                    {String(FEATURES.length).padStart(2, "0")}
                  </div>
                  <h2 className="csl-slide__title">{f.title}</h2>
                  <p className="csl-slide__desc">{f.desc}</p>
                </div>
              </section>
            ))}
          </div>

          {/* controls */}
          <button
            type="button"
            className="csl-arrow csl-arrow--prev"
            onClick={() => goTo(active - 1)}
            aria-label="Previous feature"
          >
            <ArrowLeft />
          </button>
          <button
            type="button"
            className="csl-arrow csl-arrow--next"
            onClick={() => goTo(active + 1)}
            aria-label="Next feature"
          >
            <ArrowRight />
          </button>

          {/* progress bars */}
          <div className="csl-progress" role="tablist" aria-label="Slides">
            {FEATURES.map((f, i) => (
              <button
                type="button"
                key={i}
                role="tab"
                aria-selected={i === active}
                aria-label={f.title}
                className={`csl-progress__seg${i === active ? " is-active" : ""}${i < active ? " is-done" : ""}`}
                onClick={() => goTo(i)}
                style={{ "--sl-color": f.color, "--sl-ms": `${SLIDE_MS}ms` }}
              >
                <span
                  className="csl-progress__fill"
                  data-paused={paused ? "true" : "false"}
                />
              </button>
            ))}
          </div>
        </div>
      </main>

      {/* ── Compact logo marquee ── */}
      <HomePageFooter />
    </div>
  );
}

/* ─────────────────────── icons ─────────────────────── */

function IconChat() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconNews() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Z" />
      <path d="M16 2v4H8" />
      <path d="M9 13h6M9 9h3" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function IconPulse() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function IconImage() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}

function IconBot() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M12 2a2 2 0 0 1 2 2v7H10V4a2 2 0 0 1 2-2z" />
      <circle cx="8" cy="16" r="1" />
      <circle cx="16" cy="16" r="1" />
      <path d="M8 11V8m8 3V8" />
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function ArrowLeft() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );
}
